import React, {useEffect, useRef} from 'react';
// eslint-disable-next-line no-restricted-imports -- only using type for parity
import type {Component as PanelComponent} from 'panel';

import {getReactComponentId} from 'common/util/react';

import type {SnabbdomElementProps} from '../types';
import {SnabbdomRenderer} from './snabbdom';
import {getCustomElementAttributes} from './util';

/**
 * Renderer that handles Panel-specific features for unmigrated Panel components:
 * - props
 * - params
 */
export const LegacyPanelRenderer = (
  props: SnabbdomElementProps & {
    /** Parameters to pass to the component */
    params?: Record<string, any>;
    /** Properties to set on the component */
    props?: Record<string, any>;
  },
) => {
  const ref = useRef<PanelComponent<any> | null>(null);
  const wrapperId = useRef(``);

  if (!wrapperId.current) {
    wrapperId.current = getReactComponentId();
  }

  const updateParams = () => {
    if (!props.params || !ref.current) {
      return;
    }

    ref.current.setParams(props.params);
  };

  const prevProps = useRef<any>(null);

  const updateProps = () => {
    if (!props.props || !ref.current) {
      return;
    }

    Object.entries(props.props).forEach(([key, value]) => {
      const prevValue = prevProps.current?.[key];

      if (ref.current && prevValue !== value) {
        // for some compatibility with snabbdom, check strict equal for prevProp and current prop
        // https://github.com/snabbdom/snabbdom/blob/v3.0.3/src/modules/props.ts#L15
        (ref.current as any)[key] = value;
      }
    });

    prevProps.current = props.props;
  };

  // we're doing this as a ref to ensure it happens BEFORE construction/mount and not after to make panel happy
  const initializeEventHandler = useRef<((ev: Event) => void) | null>(null);

  if (!initializeEventHandler.current) {
    initializeEventHandler.current = (ev: Event) => {
      const el = (ev as CustomEvent).detail.el as any;

      if (el.getAttribute(`rId`) === wrapperId.current) {
        ref.current = el;

        updateParams();
      }
    };

    document.addEventListener(`preComponentInitialized`, initializeEventHandler.current);
  }

  useEffect(() => {
    return () => {
      document.removeEventListener(`preComponentInitialized`, initializeEventHandler.current!);
    };
  }, []);

  useEffect(updateParams, [props.params]);
  useEffect(updateProps, [props.props]);

  const attrs: Record<string, any> = getCustomElementAttributes({
    ...props.attrs,
    className: props.attrs?.className || props.props?.className,
    rId: wrapperId.current,
  });

  return (
    <SnabbdomRenderer ref={ref} tagName={props.tagName} attrs={attrs} on={props.on} hooks={props.hooks}>
      {props.children}
    </SnabbdomRenderer>
  );
};
